/**
 * GlobalHeader — Responsive marketing and app-level navigation header.
 *
 * Desktop (≥1024px): glass topbar with logo, NavigationMenu (dropdown-capable),
 * and CTA buttons.
 * Mobile (<1024px): hamburger → slide-down panel + optional persistent bottom bar.
 *
 * Hamburger animation is handled by HamburgerButton (Framer Motion 12.x).
 * Respects prefers-reduced-motion via Motion's useReducedMotion() hook.
 * Logo implements onError fallback to logomarkSrc when horizontal fails to load.
 *
 * All button and icon colors resolve via `--primary`, adapting to any product theme.
 *
 * @package @engagexp/legendary-ui-primitives
 */

import * as React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { duration } from '@engagexp/legendary-css';
import { cn } from '../../../utils/cn';
import { Button } from '../../button';
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from '../../navigation-menu';
import { MobileSubMenu } from './SubMenu';
import { MobileBottomBar } from './MobileBottomBar';
import { HamburgerButton } from './HamburgerButton';
import styles from './global-header.module.css';
import type { MobileBottomBarProps } from './MobileBottomBar';

export type LogoSize = 28 | 32 | 36 | 40 | 44 | 48;

export interface NavSubLink {
  title: string;
  url: string;
}

export interface NavLink {
  title: string;
  url: string;
  subLinks?: NavSubLink[];
}

export interface HeaderButton {
  title: string;
  variant?:
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'outline'
    | 'outline-primary'
    | 'gradient-primary';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  onClick?: () => void;
}

export interface GlobalHeaderProps {
  logo: {
    src: string;
    alt?: string;
    href?: string;
    /** Fallback logomark src displayed via onError when horizontal logo fails. */
    logomarkSrc?: string;
  };
  /** Logo height in px. One of: 28, 32, 36, 40, 44, 48. Default: 36. */
  logoSize?: LogoSize;
  /**
   * Optional React node rendered in place of the default `<img>` logo.
   * Use this for text-based or SVG logos that need styled markup.
   * When provided, `logo.src`, `logoSize`, and `logomarkSrc` are ignored.
   */
  logoNode?: React.ReactNode;
  navLinks?: NavLink[];
  buttons?: HeaderButton[];
  mobileBottomBar?: MobileBottomBarProps;
  className?: string;
}

// Framer Motion-compatible values derived from @engagexp/legendary-css motion constants.
// Motion uses seconds; tokens use millisecond strings — divide by 1000.
const PANEL_DURATION = parseFloat(duration.base) / 1000;       // 0.25s
const PANEL_EASING: [number, number, number, number] = [0.16, 1, 0.3, 1]; // easing.emphasis

const subLinkClass =
  'block rounded-sm px-3 py-2 text-sm leading-none no-underline outline-none transition-colors' +
  ' hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground' +
  ' focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1';

const flatLinkClass =
  'group inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 text-sm' +
  ' font-medium transition-colors hover:bg-accent hover:text-accent-foreground' +
  ' focus:bg-accent focus:text-accent-foreground outline-none' +
  ' focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1';

export function GlobalHeader({
  logo,
  logoSize = 36,
  logoNode,
  navLinks = [],
  buttons = [],
  mobileBottomBar,
  className,
}: GlobalHeaderProps) {
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);
  const [logoError, setLogoError] = React.useState(false);

  const panelId = React.useId();
  const toggleId = React.useId();
  const prefersReducedMotion = useReducedMotion();

  // Refs for focus management — keyboard a11y (WCAG 2.1 AA)
  const hamburgerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  // Close panel and return focus to hamburger trigger
  const closeMobile = React.useCallback(() => {
    setIsMobileOpen(false);
    hamburgerRef.current?.focus();
  }, []);

  // Move focus into panel on open; SR users hear the nav content immediately
  React.useEffect(() => {
    if (isMobileOpen) {
      const first = panelRef.current?.querySelector<HTMLElement>(
        'a[href], button:not([disabled])',
      );
      first?.focus();
    }
  }, [isMobileOpen]);

  // Reset logo fallback when the src prop changes
  React.useEffect(() => { setLogoError(false); }, [logo.src]);
  const resolvedLogoSrc = logoError && logo.logomarkSrc ? logo.logomarkSrc : logo.src;

  return (
    <>
      <header className={cn(styles.root, className)}>
        {/* Brand accent bar — rare-blue → epic-purple → legendary-orange */}
        <div className={styles.accentBar} aria-hidden="true" />

        <div className={styles.inner}>
          {/* Brand — logoNode override or image with logomark fallback */}
          <a href={logo.href ?? '/'} className={styles.brand} aria-label="Go to homepage">
            {logoNode ?? (
              <img
                src={resolvedLogoSrc}
                alt={logo.alt ?? ''}
                className={styles.logo}
                data-size={logoSize}
                onError={() => { if (!logoError) setLogoError(true); }}
              />
            )}
          </a>

          {/* Desktop NavigationMenu */}
          {navLinks.length > 0 && (
            <div className={styles.desktopNav}>
              <NavigationMenu aria-label="Main navigation">
                <NavigationMenuList>
                  {navLinks.map((link) =>
                    link.subLinks && link.subLinks.length > 0 ? (
                      <NavigationMenuItem key={`${link.title}-${link.url}`}>
                        <NavigationMenuTrigger className="bg-transparent">
                          {link.title}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                          <ul className={styles.subMenuList}>
                            {link.subLinks.map((sub) => (
                              <li key={sub.url}>
                                <NavigationMenuLink href={sub.url} className={subLinkClass}>
                                  {sub.title}
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    ) : (
                      <NavigationMenuItem key={`${link.title}-${link.url}`}>
                        <NavigationMenuLink href={link.url} className={flatLinkClass}>
                          {link.title}
                        </NavigationMenuLink>
                      </NavigationMenuItem>
                    ),
                  )}
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          )}

          {/* Desktop CTA Buttons */}
          {buttons.length > 0 && (
            <div className={styles.trailing}>
              {buttons.map((btn, i) =>
                btn.href ? (
                  <Button key={i} variant={btn.variant ?? (i === 0 ? 'outline-primary' : 'gradient-primary')} size={btn.size ?? 'sm'} asChild>
                    <a href={btn.href}>{btn.title}</a>
                  </Button>
                ) : (
                  <Button key={i} variant={btn.variant ?? (i === 0 ? 'outline-primary' : 'gradient-primary')} size={btn.size ?? 'sm'} onClick={btn.onClick}>
                    {btn.title}
                  </Button>
                ),
              )}
            </div>
          )}

          {/* Mobile Hamburger — extracted to keep GlobalHeader under 300 lines */}
          <HamburgerButton
            ref={hamburgerRef}
            id={toggleId}
            isOpen={isMobileOpen}
            panelId={panelId}
            onToggle={() => setIsMobileOpen((v) => !v)}
          />
        </div>

        {/* Mobile Slide-Down Panel — AnimatePresence for smooth enter/exit */}
        <AnimatePresence>
          {isMobileOpen && (
            <motion.div
              ref={panelRef}
              id={panelId}
              role="region"
              aria-label="Mobile navigation menu"
              className={styles.mobilePanel}
              initial={prefersReducedMotion ? undefined : { height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={prefersReducedMotion ? undefined : { height: 0, opacity: 0 }}
              transition={prefersReducedMotion ? { duration: 0 } : { duration: PANEL_DURATION, ease: PANEL_EASING }}
              onKeyDown={(e) => { if (e.key === 'Escape') closeMobile(); }}
            >
              <nav aria-label="Main mobile navigation">
                {navLinks.map((link) =>
                  link.subLinks && link.subLinks.length > 0 ? (
                    <MobileSubMenu key={`${link.title}-${link.url}`} link={link} onNavigate={closeMobile} />
                  ) : (
                    <a key={`${link.title}-${link.url}`} href={link.url} className={styles.mobileNavLink} onClick={closeMobile}>
                      {link.title}
                    </a>
                  ),
                )}
              </nav>

              {buttons.length > 0 && (
                <div className={styles.mobilePanelButtons}>
                  {buttons.map((btn, i) =>
                    btn.href ? (
                      <Button key={i} variant={btn.variant ?? (i === 0 ? 'outline-primary' : 'gradient-primary')} size="md" className="w-full" asChild>
                        <a href={btn.href} onClick={closeMobile}>{btn.title}</a>
                      </Button>
                    ) : (
                      <Button key={i} variant={btn.variant ?? (i === 0 ? 'outline-primary' : 'gradient-primary')} size="md" className="w-full" onClick={() => { btn.onClick?.(); closeMobile(); }}>
                        {btn.title}
                      </Button>
                    ),
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Persistent Mobile Bottom Bar */}
      {mobileBottomBar && <MobileBottomBar {...mobileBottomBar} />}
    </>
  );
}

GlobalHeader.displayName = 'GlobalHeader';
